import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { casesAPI } from '../lib/api';
import { useConfetti } from '../hooks/useConfetti';
import { QualityBadge, scoreCase } from '../lib/dataQuality';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Search, Plus, Eye, Briefcase, Home, Shield, Filter, X, Trash2, RotateCcw, Loader2,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { LenderAutocomplete } from '../components/LenderAutocomplete';
import { ScreenshotImport } from '../components/ScreenshotImport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CASE_STATUSES = [
  { key: 'new_lead', label: 'New Lead', color: 'bg-blue-100 text-blue-800' },
  { key: 'fact_find_complete', label: 'Fact Find Complete', color: 'bg-purple-100 text-purple-800' },
  { key: 'application_submitted', label: 'Application Submitted', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'valuation_booked', label: 'Valuation Booked', color: 'bg-orange-100 text-orange-800' },
  { key: 'offer_issued', label: 'Offer Issued', color: 'bg-indigo-100 text-indigo-800' },
  { key: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { key: 'lost_case', label: 'Lost Case', color: 'bg-red-100 text-red-800' },
  { key: 'review_due', label: 'Review Due', color: 'bg-amber-100 text-amber-800' },
  { key: 'for_review', label: 'For Review', color: 'bg-amber-100 text-amber-800' },
];

const MORTGAGE_TYPES = [
  { key: 'purchase', label: 'Purchase' },
  { key: 'remortgage', label: 'Remortgage' },
  { key: 'remortgage_additional_borrowing', label: 'Remortgage + Additional Borrowing' },
  { key: 'product_transfer', label: 'Product Transfer' },
  { key: 'further_advance', label: 'Further Advance' },
];

const INSURANCE_TYPES = [
  { key: 'life_insurance', label: 'Life Insurance' },
  { key: 'buildings_insurance', label: 'Buildings Insurance' },
  { key: 'home_insurance', label: 'Home Insurance' },
];

const INSURANCE_COVER_TYPES = [
  { key: 'level_term', label: 'Level Term' },
  { key: 'decreasing_term', label: 'Decreasing Term' },
  { key: 'increasing_term', label: 'Increasing Term' },
  { key: 'whole_of_life', label: 'Whole of Life' },
];

const REPAYMENT_TYPES = [
  { key: 'interest_only', label: 'Interest Only' },
  { key: 'repayment', label: 'Repayment' },
];

const PROPERTY_TYPES = [
  { key: 'residential', label: 'Residential' },
  { key: 'buy_to_let', label: 'Buy To Let' },
];

const INTEREST_RATE_TYPES = [
  { key: 'fixed', label: 'Fixed' },
  { key: 'variable', label: 'Variable' },
  { key: 'discounted', label: 'Discounted' },
  { key: 'tracker', label: 'Tracker' },
  { key: 'capped', label: 'Capped' },
];

const formatStatus = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-';
const formatCurrency = (v) => v ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(v) : '-';

// Build a list of "additional applicant" names to show under the primary client on a case row.
// Priority: applicant_names (if set on the case) — these are the applicants explicitly chosen for THIS case.
// Otherwise fall back to the client's full additional_applicants list.
const getCaseExtraApplicants = (c) => {
  const primary = (c.client_name || '').trim().toLowerCase();
  if (Array.isArray(c.applicant_names) && c.applicant_names.length > 0) {
    return c.applicant_names.filter(n => n && n.trim().toLowerCase() !== primary);
  }
  if (Array.isArray(c.client_additional_applicants)) {
    return c.client_additional_applicants
      .map(a => `${a?.first_name || ''} ${a?.last_name || ''}`.trim())
      .filter(Boolean);
  }
  return [];
};
const ExtraApplicantsList = ({ names }) => {
  if (!names || !names.length) return null;
  return (
    <div className="mt-0.5 space-y-0.5">
      {names.map((n, i) => (
        <p key={i} className="text-xs text-slate-500 dark:text-slate-400 leading-tight font-normal">+ {n}</p>
      ))}
    </div>
  );
};
const formatDate = (d) => {
  if (!d) return '-';
  const parts = d.split('T')[0].split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};
const getStatusColor = (s) => CASE_STATUSES.find(st => st.key === s)?.color || 'bg-slate-100 text-slate-800';

// Currency input: show raw number when focused, formatted £ when blurred
const CurrencyInput = ({ value, onChange, ...props }) => {
  const [display, setDisplay] = React.useState(value || '');
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    if (!focused) setDisplay(value || '');
  }, [value, focused]);

  const fmtDisplay = (v) => {
    const num = parseFloat(v);
    if (isNaN(num) || !v) return v;
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(num);
  };

  return (
    <Input
      {...props}
      type={focused ? 'number' : 'text'}
      value={focused ? display : (display ? fmtDisplay(display) : '')}
      onChange={(e) => { setDisplay(e.target.value); onChange(e); }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

// Clean empty strings to null for Pydantic compatibility
const cleanData = (obj) => {
  const cleaned = {};
  for (const [k, v] of Object.entries(obj)) {
    cleaned[k] = (v === '' || v === undefined) ? null : v;
  }
  return cleaned;
};

const Cases = () => {
  const navigate = useNavigate();
  const fireConfetti = useConfetti();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', product_type: '', commission_status: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('mortgage');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);

  // Bulk delete state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // New case form
  const [newCase, setNewCase] = useState({ product_type: 'mortgage', client_id: '', mortgage_type: '', insurance_type: '', status: 'new_lead' });
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientSearchRef = useRef(null);

  useEffect(() => { loadCases(); }, [filters]); // eslint-disable-line

  const loadCases = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.product_type) params.product_type = filters.product_type;
      if (filters.commission_status) params.commission_status = filters.commission_status;
      if (search) params.search = search;
      const data = await casesAPI.getAll(params);
      setCases(data.cases || []);
      setSelectedIds(new Set()); // clear selection on reload
    } catch (err) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadCases();

  // Client search for new case form
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/clients/search?q=${encodeURIComponent(clientSearch)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setClientResults(data.clients || data || []);
          if (clientSearch.length > 0) setClientDropdownOpen(true);
        }
      } catch (err) { console.error(err); }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  useEffect(() => {
    const handleClick = (e) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(e.target)) setClientDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    // Default applicant_names: primary + all additional applicants (full names)
    const primaryName = `${client.first_name || ''} ${client.last_name || ''}`.trim();
    const extras = (Array.isArray(client.additional_applicants) ? client.additional_applicants : [])
      .map(a => `${a?.first_name || ''} ${a?.last_name || ''}`.trim())
      .filter(Boolean);
    const allNames = [primaryName, ...extras].filter(Boolean);
    setNewCase({
      ...newCase,
      client_id: client.client_id,
      limited_company: client.company_name && client.company_name.trim() ? 'yes' : 'no',
      applicant_names: allNames,
    });
    setClientSearch(`${client.first_name} ${client.last_name}`);
    setClientDropdownOpen(false);
  };

  const toggleApplicant = (name) => {
    setNewCase(prev => {
      const current = Array.isArray(prev.applicant_names) ? prev.applicant_names : [];
      const next = current.includes(name)
        ? current.filter(n => n !== name)
        : [...current, name];
      return { ...prev, applicant_names: next };
    });
  };

  const handleCreateCase = async () => {
    try {
      const caseData = cleanData({ ...newCase });
      ['loan_amount', 'property_value', 'interest_rate', 'monthly_premium', 'sum_assured', 'proc_fee_total', 'commission_percentage', 'proc_fee_value', 'client_fee', 'deposit'].forEach(f => {
        if (caseData[f] !== null && caseData[f] !== undefined && caseData[f] !== '') caseData[f] = parseFloat(caseData[f]);
        else caseData[f] = null;
      });
      ['term_years', 'fixed_rate_period', 'initial_product_term'].forEach(f => {
        if (caseData[f] !== null && caseData[f] !== undefined && caseData[f] !== '') caseData[f] = parseInt(caseData[f]);
        else caseData[f] = null;
      });
      if (caseData.proc_fee_total && caseData.commission_percentage) {
        caseData.gross_commission = Math.round((caseData.proc_fee_total * caseData.commission_percentage / 100) * 100) / 100;
      }
      for (const k of Object.keys(caseData)) {
        if (typeof caseData[k] === 'number' && isNaN(caseData[k])) caseData[k] = null;
      }
      // Ensure status fields have valid defaults
      if (!caseData.commission_status) caseData.commission_status = 'pending';
      if (!caseData.client_fee_status) caseData.client_fee_status = 'pending';
      // Clear paid dates if status isn't paid
      if (caseData.commission_status !== 'paid') caseData.commission_paid_date = null;
      if (caseData.client_fee_status !== 'paid') caseData.client_fee_paid_date = null;
      await casesAPI.create(caseData);
      toast.success('Case created successfully');
      setShowAddDialog(false);
      resetNewCase();
      loadCases();
    } catch (err) {
      const msg = typeof err.message === 'string' ? err.message : 'Failed to create case';
      toast.error(msg);
    }
  };

  const handleDeleteCase = async () => {
    if (!caseToDelete) return;
    try {
      await casesAPI.delete(caseToDelete.case_id);
      toast.success('Case deleted');
      setDeleteDialogOpen(false);
      setCaseToDelete(null);
      loadCases();
    } catch (err) {
      toast.error(err.message || 'Failed to delete case');
    }
  };

  const resetNewCase = () => {
    setNewCase({ product_type: 'mortgage', client_id: '', mortgage_type: '', insurance_type: '', status: 'new_lead', applicant_names: [] });
    setClientSearch('');
    setSelectedClient(null);
  };

  // Bulk selection helpers
  const sortByDate = (arr) => [...arr].sort((a, b) => {
    const da = a.date_application_submitted || a.created_at || '';
    const db = b.date_application_submitted || b.created_at || '';
    return db.localeCompare(da);
  });

  // ── Column sort state (separate per tab) ─────────────────────────────────
  const [mortgageSort, setMortgageSort] = useState({ key: null, dir: 'asc' });
  const [insuranceSort, setInsuranceSort] = useState({ key: null, dir: 'asc' });

  // Map a sort key to a comparable value for a case row
  const caseSortValue = (c, key) => {
    switch (key) {
      case 'client_name':       return (c.client_name || '').toLowerCase();
      case 'mortgage_type':     return (c.mortgage_type || '').toLowerCase();
      case 'insurance_type':    return (c.insurance_type || '').toLowerCase();
      case 'lender_name':       return (c.lender_name || '').toLowerCase();
      case 'insurance_provider':return (c.insurance_provider || c.lender_name || '').toLowerCase();
      case 'cover_type':        return (c.insurance_cover_type || '').toLowerCase();
      case 'loan_amount':       return c.loan_amount || 0;
      case 'property_value':    return c.property_value || 0;
      case 'monthly_premium':   return c.monthly_premium || 0;
      case 'sum_assured':       return c.sum_assured || 0;
      case 'status':            return (c.status || '').toLowerCase();
      case 'commission':        return c.gross_commission || 0;
      case 'commission_status': return (c.commission_status || '').toLowerCase();
      case 'quality':           return scoreCase(c)?.percent ?? 0;
      case 'expiry':            return c.product_expiry_date || '';
      default:                  return '';
    }
  };

  const sortRows = (rows, sort) => {
    if (!sort.key) return rows;
    const sorted = [...rows].sort((a, b) => {
      const va = caseSortValue(a, sort.key);
      const vb = caseSortValue(b, sort.key);
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      return String(va).localeCompare(String(vb));
    });
    return sort.dir === 'desc' ? sorted.reverse() : sorted;
  };

  const handleSortClick = (sort, setSort, key) => {
    if (sort.key === key) setSort({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    else setSort({ key, dir: 'asc' });
  };

  const SortHead = ({ label, sortKey, sort, setSort, className = '' }) => {
    const active = sort.key === sortKey;
    const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <TableHead className={className}>
        <button
          type="button"
          onClick={() => handleSortClick(sort, setSort, sortKey)}
          className={`inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-white transition-colors ${active ? 'text-slate-900 dark:text-white font-semibold' : ''}`}
        >
          {label}
          <Icon className={`h-3.5 w-3.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
        </button>
      </TableHead>
    );
  };

  const mortgageCases = sortRows(sortByDate(cases.filter(c => c.product_type === 'mortgage')), mortgageSort);
  const insuranceCases = sortRows(sortByDate(cases.filter(c => c.product_type === 'insurance')), insuranceSort);

  const currentTabCases = activeTab === 'mortgage' ? mortgageCases : insuranceCases;
  const allTabSelected = currentTabCases.length > 0 && currentTabCases.every(c => selectedIds.has(c.case_id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allTabSelected) {
      currentTabCases.forEach(c => next.delete(c.case_id));
    } else {
      currentTabCases.forEach(c => next.add(c.case_id));
    }
    setSelectedIds(next);
  };

  const toggleSelectOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    for (const id of selectedIds) {
      try {
        await casesAPI.delete(id);
        successCount++;
      } catch {
        failCount++;
      }
    }
    setBulkDeleting(false);
    setBulkDeleteDialogOpen(false);
    if (successCount > 0) toast.success(`${successCount} case${successCount > 1 ? 's' : ''} deleted`);
    if (failCount > 0) toast.error(`${failCount} case${failCount > 1 ? 's' : ''} could not be deleted`);
    loadCases();
  };

  const clearFilters = () => setFilters({ status: '', product_type: '', commission_status: '' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="cases-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>Cases</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Manage mortgage and insurance cases</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" onClick={() => setShowAddDialog(true)} data-testid="add-case-btn">
          <Plus className="h-4 w-4 mr-2" />New Case
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <Input placeholder="Search by client name..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} className="flex-1" data-testid="case-search-input" />
              <Button onClick={handleSearch}><Search className="h-4 w-4 mr-2" />Search</Button>
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}><Filter className="h-4 w-4 mr-2" />Filters</Button>
            </div>
          </div>
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {CASE_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Commission Status</Label>
                <Select value={filters.commission_status || 'all'} onValueChange={(v) => setFilters({ ...filters, commission_status: v === 'all' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="submitted_to_lender">Submitted</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="clawed_back">Clawed Back</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters} className="text-slate-500"><X className="h-4 w-4 mr-1" />Clear</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Delete Bar */}
      {someSelected && (
        <div className="flex items-center gap-3 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <span className="text-sm font-medium text-red-700 dark:text-red-400">
            {selectedIds.size} case{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-500 hover:text-slate-700 h-7 px-2"
            onClick={() => setSelectedIds(new Set())}
          >
            <X className="h-3.5 w-3.5 mr-1" />Deselect
          </Button>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 h-7"
            onClick={() => setBulkDeleteDialogOpen(true)}
            data-testid="bulk-delete-cases-btn"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />Delete Selected
          </Button>
        </div>
      )}

      {/* Tabs: Mortgage / Insurance */}
      <Tabs value={activeTab} onValueChange={(tab) => { setActiveTab(tab); }}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="mortgage" className="flex items-center gap-2" data-testid="mortgage-tab">
            <Home className="h-4 w-4" />Mortgage ({mortgageCases.length})
          </TabsTrigger>
          <TabsTrigger value="insurance" className="flex items-center gap-2" data-testid="insurance-tab">
            <Shield className="h-4 w-4" />Insurance ({insuranceCases.length})
          </TabsTrigger>
        </TabsList>

        {/* Mortgage Cases */}
        <TabsContent value="mortgage">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg"><Home className="h-5 w-5 text-red-600" />Mortgage Cases</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {mortgageCases.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No mortgage cases found</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[44px] pl-4">
                          <Checkbox
                            checked={allTabSelected}
                            onCheckedChange={toggleSelectAll}
                            aria-label="Select all mortgage cases"
                            data-testid="select-all-mortgage"
                          />
                        </TableHead>
                        <SortHead label="Client"         sortKey="client_name"       sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Type"           sortKey="mortgage_type"     sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Lender"         sortKey="lender_name"       sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Loan Amount"    sortKey="loan_amount"       sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Property Value" sortKey="property_value"    sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Status"         sortKey="status"            sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Commission"     sortKey="commission"        sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Comm. Status"   sortKey="commission_status" sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Quality"        sortKey="quality"           sort={mortgageSort} setSort={setMortgageSort} />
                        <SortHead label="Expiry"         sortKey="expiry"            sort={mortgageSort} setSort={setMortgageSort} />
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mortgageCases.map((c) => (
                        <TableRow
                          key={c.case_id}
                          className={`cursor-pointer transition-colors ${selectedIds.has(c.case_id) ? 'bg-red-50/60 dark:bg-red-900/10' : c.status === 'completed' && c.commission_status === 'paid' ? 'bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/40 dark:text-slate-100' : c.status === 'completed' && c.commission_status !== 'paid' ? 'bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/40 dark:hover:bg-yellow-900/50 dark:text-yellow-100' : c.status === 'lost_case' ? 'bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/40 dark:text-slate-100' : 'hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-100'}`}
                          onClick={() => navigate(`/cases/${c.case_id}`)}
                          data-testid={`case-row-${c.case_id}`}
                        >
                          <TableCell className="pl-4" onClick={(e) => { e.stopPropagation(); toggleSelectOne(c.case_id); }}>
                            <Checkbox
                              checked={selectedIds.has(c.case_id)}
                              onCheckedChange={() => toggleSelectOne(c.case_id)}
                              aria-label={`Select case ${c.case_id}`}
                              data-testid={`select-case-${c.case_id}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {c.client_name || '-'}
                            <ExtraApplicantsList names={getCaseExtraApplicants(c)} />
                          </TableCell>
                          <TableCell>{formatStatus(c.mortgage_type)}</TableCell>
                          <TableCell>{c.lender_name || '-'}</TableCell>
                          <TableCell>{formatCurrency(c.loan_amount)}</TableCell>
                          <TableCell>{formatCurrency(c.property_value)}</TableCell>
                          <TableCell><Badge className={getStatusColor(c.status)}>{formatStatus(c.status)}</Badge></TableCell>
                          <TableCell>{formatCurrency(c.gross_commission)}</TableCell>
                          <TableCell>
                            {c.commission_status === 'paid'
                              ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Paid</Badge>
                              : <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">Pending</Badge>}
                          </TableCell>
                          <TableCell><QualityBadge record={c} type="case" /></TableCell>
                          <TableCell className="text-sm">{formatDate(c.product_expiry_date)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" onClick={() => navigate(`/cases/${c.case_id}`)} data-testid={`view-case-${c.case_id}`}><Eye className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" onClick={() => { setCaseToDelete(c); setDeleteDialogOpen(true); }} data-testid={`delete-case-${c.case_id}`}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insurance Cases */}
        <TabsContent value="insurance">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg"><Shield className="h-5 w-5 text-blue-600" />Insurance Cases</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {insuranceCases.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No insurance cases found</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[44px] pl-4">
                          <Checkbox
                            checked={allTabSelected}
                            onCheckedChange={toggleSelectAll}
                            aria-label="Select all insurance cases"
                            data-testid="select-all-insurance"
                          />
                        </TableHead>
                        <SortHead label="Client"          sortKey="client_name"        sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Insurance Type"  sortKey="insurance_type"     sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Provider"        sortKey="insurance_provider" sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Cover Type"      sortKey="cover_type"         sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Monthly Premium" sortKey="monthly_premium"    sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Sum Assured"     sortKey="sum_assured"        sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Status"          sortKey="status"             sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Commission"      sortKey="commission"         sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Comm. Status"    sortKey="commission_status"  sort={insuranceSort} setSort={setInsuranceSort} />
                        <SortHead label="Quality"         sortKey="quality"            sort={insuranceSort} setSort={setInsuranceSort} />
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {insuranceCases.map((c) => (
                        <TableRow
                          key={c.case_id}
                          className={`cursor-pointer transition-colors ${selectedIds.has(c.case_id) ? 'bg-red-50/60 dark:bg-red-900/10' : c.status === 'completed' && c.commission_status === 'paid' ? 'bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/40 dark:text-slate-100' : c.status === 'completed' && c.commission_status !== 'paid' ? 'bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/40 dark:hover:bg-yellow-900/50 dark:text-yellow-100' : c.status === 'lost_case' ? 'bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/40 dark:text-slate-100' : 'hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-100'}`}
                          onClick={() => navigate(`/cases/${c.case_id}`)}
                          data-testid={`case-row-${c.case_id}`}
                        >
                          <TableCell className="pl-4" onClick={(e) => { e.stopPropagation(); toggleSelectOne(c.case_id); }}>
                            <Checkbox
                              checked={selectedIds.has(c.case_id)}
                              onCheckedChange={() => toggleSelectOne(c.case_id)}
                              aria-label={`Select case ${c.case_id}`}
                              data-testid={`select-case-ins-${c.case_id}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {c.client_name || '-'}
                            <ExtraApplicantsList names={getCaseExtraApplicants(c)} />
                          </TableCell>
                          <TableCell>{formatStatus(c.insurance_type)}</TableCell>
                          <TableCell>{c.insurance_provider || c.lender_name || '-'}</TableCell>
                          <TableCell>{formatStatus(c.insurance_cover_type)}</TableCell>
                          <TableCell>{formatCurrency(c.monthly_premium)}</TableCell>
                          <TableCell>{formatCurrency(c.sum_assured)}</TableCell>
                          <TableCell><Badge className={getStatusColor(c.status)}>{formatStatus(c.status)}</Badge></TableCell>
                          <TableCell>{formatCurrency(c.gross_commission)}</TableCell>
                          <TableCell>
                            {c.commission_status === 'paid'
                              ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Paid</Badge>
                              : <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">Pending</Badge>}
                          </TableCell>
                          <TableCell><QualityBadge record={c} type="case" /></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" onClick={() => navigate(`/cases/${c.case_id}`)}><Eye className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" onClick={() => { setCaseToDelete(c); setDeleteDialogOpen(true); }} data-testid={`delete-case-ins-${c.case_id}`}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Single Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Case</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this case ({caseToDelete?.case_id})? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCase} data-testid="confirm-delete-case-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {selectedIds.size} Case{selectedIds.size > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.size} selected case{selectedIds.size > 1 ? 's' : ''}? This will also delete all associated tasks. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkDeleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="confirm-bulk-delete-cases-btn">
              {bulkDeleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : `Delete ${selectedIds.size} Case${selectedIds.size > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Case Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetNewCase(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Create New Case</DialogTitle>
                <DialogDescription>Add a new mortgage or insurance case.</DialogDescription>
              </div>
              <Button variant="outline" size="sm" data-testid="clear-case-form-btn" className="text-xs h-8 shrink-0" onClick={resetNewCase}>
                <RotateCcw className="h-3 w-3 mr-1" /> Clear Form
              </Button>
            </div>
          </DialogHeader>

          <div className="pb-2">
            <ScreenshotImport type="case" onExtracted={(data) => {
              setNewCase(prev => ({
                ...prev,
                lender_name: data.lender_name || prev.lender_name,
                loan_amount: data.loan_amount ? String(data.loan_amount) : prev.loan_amount,
                property_value: data.property_value ? String(data.property_value) : prev.property_value,
                interest_rate: data.interest_rate ? String(data.interest_rate) : prev.interest_rate,
                interest_rate_type: data.interest_rate_type || prev.interest_rate_type,
                term_years: data.term_years ? String(data.term_years) : prev.term_years,
                initial_product_term: data.initial_product_term ? String(data.initial_product_term) : prev.initial_product_term,
                mortgage_type: data.mortgage_type || prev.mortgage_type,
                property_type: data.property_type || prev.property_type,
                repayment_type: data.repayment_type || prev.repayment_type,
                case_reference: data.case_reference || prev.case_reference,
                security_address: data.security_address || prev.security_address,
                security_postcode: data.security_postcode || prev.security_postcode,
                product_type: data.product_type || prev.product_type,
                ltv: data.ltv ? String(data.ltv) : prev.ltv,
                deposit: data.deposit ? String(data.deposit) : prev.deposit,
                deposit_source: data.deposit_source || prev.deposit_source,
                date_application_submitted: data.date_application_submitted || prev.date_application_submitted,
                expected_completion_date: data.expected_completion_date || prev.expected_completion_date,
                product_start_date: data.product_start_date || prev.product_start_date,
                product_expiry_date: data.product_expiry_date || prev.product_expiry_date,
                insurance_type: data.insurance_type || prev.insurance_type,
                insurance_provider: data.insurance_provider || prev.insurance_provider,
                insurance_reference: data.insurance_reference || prev.insurance_reference,
                insurance_cover_type: data.insurance_cover_type || prev.insurance_cover_type,
                guaranteed_or_reviewable: data.guaranteed_or_reviewable || prev.guaranteed_or_reviewable,
                monthly_premium: data.monthly_premium ? String(data.monthly_premium) : prev.monthly_premium,
                sum_assured: data.sum_assured ? String(data.sum_assured) : prev.sum_assured,
                in_trust: (data.in_trust !== null && data.in_trust !== undefined) ? data.in_trust : prev.in_trust,
                commission_status: data.commission_status || prev.commission_status,
                commission_paid_date: data.commission_paid_date || prev.commission_paid_date,
                client_fee_status: data.client_fee_status || prev.client_fee_status,
                client_fee_paid_date: data.client_fee_paid_date || prev.client_fee_paid_date,
              }));
            }} />
          </div>

          <div className="space-y-6 py-4">
            {/* Client Search */}
            <div className="space-y-2" ref={clientSearchRef}>
              <Label>Client *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search client by name or email..."
                  className="pl-10"
                  value={clientSearch}
                  onChange={(e) => { setClientSearch(e.target.value); setSelectedClient(null); setNewCase({ ...newCase, client_id: '' }); }}
                  onFocus={() => { if (clientResults.length) setClientDropdownOpen(true); }}
                  data-testid="case-client-search"
                />
                {clientDropdownOpen && clientResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {clientResults.map((c) => {
                      const extras = (Array.isArray(c.additional_applicants) ? c.additional_applicants : [])
                        .map(a => `${a?.first_name || ''} ${a?.last_name || ''}`.trim())
                        .filter(Boolean);
                      return (
                        <button key={c.client_id} className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm dark:text-slate-100" data-testid={`client-option-${c.client_id}`} onClick={() => handleSelectClient(c)}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{c.first_name} {c.last_name}</span>
                            {c.email && <span className="text-xs text-slate-500">({c.email})</span>}
                          </div>
                          {extras.length > 0 && (
                            <div className="mt-0.5 space-y-0.5 pl-2">
                              {extras.map((n, i) => (
                                <p key={i} className="text-xs text-slate-500 dark:text-slate-400 leading-tight">+ {n}</p>
                              ))}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {selectedClient && <p className="text-sm text-green-600">Selected: {selectedClient.first_name} {selectedClient.last_name}</p>}

              {/* Applicants checkbox list — pick which applicants are on this case */}
              {selectedClient && (() => {
                const primaryName = `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim();
                const extras = (Array.isArray(selectedClient.additional_applicants) ? selectedClient.additional_applicants : [])
                  .map(a => `${a?.first_name || ''} ${a?.last_name || ''}`.trim())
                  .filter(Boolean);
                const all = [primaryName, ...extras];
                const selected = Array.isArray(newCase.applicant_names) ? newCase.applicant_names : [];
                return (
                  <div className="mt-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
                    <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide mb-2">
                      Applicants on this case
                    </p>
                    <div className="space-y-2">
                      {all.map((name) => (
                        <label key={name} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={selected.includes(name)}
                            onCheckedChange={() => toggleApplicant(name)}
                            data-testid={`applicant-tick-${name}`}
                          />
                          <span className="text-sm text-slate-700 dark:text-slate-200">{name}</span>
                          {name === primaryName && (
                            <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">primary</span>
                          )}
                        </label>
                      ))}
                    </div>
                    {selected.length === 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">Select at least one applicant.</p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Product Type Toggle */}
            <div className="space-y-2">
              <Label>Case Type *</Label>
              <div className="flex gap-2">
                <Button type="button" variant={newCase.product_type === 'mortgage' ? 'default' : 'outline'} className={newCase.product_type === 'mortgage' ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => setNewCase({ ...newCase, product_type: 'mortgage' })} data-testid="case-type-mortgage"><Home className="h-4 w-4 mr-2" />Mortgage</Button>
                <Button type="button" variant={newCase.product_type === 'insurance' ? 'default' : 'outline'} className={newCase.product_type === 'insurance' ? 'bg-blue-600 hover:bg-blue-700' : ''} onClick={() => setNewCase({ ...newCase, product_type: 'insurance' })} data-testid="case-type-insurance"><Shield className="h-4 w-4 mr-2" />Insurance</Button>
              </div>
            </div>

            {/* Mortgage Fields */}
            {newCase.product_type === 'mortgage' && (
              <div className="grid grid-cols-2 gap-4 pt-2">
                <h3 className="col-span-2 font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2 pb-1 border-b border-slate-200 dark:border-slate-700"><Home className="h-4 w-4" />Mortgage Details</h3>
                <div className="space-y-2">
                  <Label>Mortgage Type</Label>
                  <Select value={newCase.mortgage_type || 'none'} onValueChange={(v) => setNewCase({ ...newCase, mortgage_type: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="case-mortgage-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem>{MORTGAGE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Lender Name</Label><LenderAutocomplete value={newCase.lender_name || ''} onChange={(e) => setNewCase({ ...newCase, lender_name: e.target.value })} data-testid="case-lender" /></div>
                <div className="space-y-2"><Label>Loan Amount (£)</Label><CurrencyInput value={newCase.loan_amount || ''} onChange={(e) => setNewCase({ ...newCase, loan_amount: e.target.value })} data-testid="case-loan-amount" /></div>
                <div className="space-y-2"><Label>Property Value (£)</Label><CurrencyInput value={newCase.property_value || ''} onChange={(e) => setNewCase({ ...newCase, property_value: e.target.value })} data-testid="case-property-value" /></div>
                <div className="space-y-2"><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={newCase.interest_rate || ''} onChange={(e) => setNewCase({ ...newCase, interest_rate: e.target.value })} data-testid="case-interest-rate" /></div>
                <div className="space-y-2">
                  <Label>Interest Rate Type</Label>
                  <Select value={newCase.interest_rate_type || 'none'} onValueChange={(v) => setNewCase({ ...newCase, interest_rate_type: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="case-interest-rate-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem>{INTEREST_RATE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Initial Product Term (Years)</Label><Input type="number" value={newCase.initial_product_term || ''} onChange={(e) => setNewCase({ ...newCase, initial_product_term: e.target.value })} data-testid="case-initial-product-term" /></div>
                <div className="space-y-2"><Label>Term (Years)</Label><Input type="number" value={newCase.term_years || ''} onChange={(e) => setNewCase({ ...newCase, term_years: e.target.value })} data-testid="case-term" /></div>
                <div className="space-y-2"><Label>Deposit Source</Label><Input value={newCase.deposit_source || ''} onChange={(e) => setNewCase({ ...newCase, deposit_source: e.target.value })} data-testid="case-deposit-source" /></div>
                <div className="space-y-2"><Label>Deposit (£)</Label><CurrencyInput value={newCase.deposit || ''} onChange={(e) => setNewCase({ ...newCase, deposit: e.target.value })} data-testid="case-deposit" /></div>
                <div className="space-y-2">
                  <Label>Repayment Type</Label>
                  <Select value={newCase.repayment_type || 'none'} onValueChange={(v) => setNewCase({ ...newCase, repayment_type: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="case-repayment-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem>{REPAYMENT_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Property Type</Label>
                  <Select value={newCase.property_type || 'none'} onValueChange={(v) => setNewCase({ ...newCase, property_type: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="case-property-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem>{PROPERTY_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Case Reference Number</Label><Input value={newCase.case_reference || ''} onChange={(e) => setNewCase({ ...newCase, case_reference: e.target.value })} data-testid="case-reference" /></div>
                <div className="space-y-2">
                  <Label>Limited Company</Label>
                  <Select value={newCase.limited_company || 'no'} onValueChange={(v) => setNewCase({ ...newCase, limited_company: v })}>
                    <SelectTrigger data-testid="case-limited-company"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Security Property Address</Label><Input value={newCase.security_address || ''} onChange={(e) => setNewCase({ ...newCase, security_address: e.target.value })} placeholder="Property address" data-testid="case-security-address" /></div>
                  <div className="space-y-2"><Label>Security Post Code</Label><Input value={newCase.security_postcode || ''} onChange={(e) => setNewCase({ ...newCase, security_postcode: e.target.value })} placeholder="e.g. SW1A 1AA" data-testid="case-security-postcode" /></div>
                </div>
              </div>
            )}

            {/* Insurance Fields */}
            {newCase.product_type === 'insurance' && (
              <div className="grid grid-cols-2 gap-4 pt-2">
                <h3 className="col-span-2 font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2 pb-1 border-b border-slate-200 dark:border-slate-700"><Shield className="h-4 w-4" />Insurance Details</h3>
                <div className="space-y-2">
                  <Label>Insurance Type</Label>
                  <Select value={newCase.insurance_type || 'none'} onValueChange={(v) => setNewCase({ ...newCase, insurance_type: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="case-insurance-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem>{INSURANCE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Term (Years)</Label><Input type="number" value={newCase.term_years || ''} onChange={(e) => setNewCase({ ...newCase, term_years: e.target.value })} data-testid="case-insurance-term" /></div>
                <div className="space-y-2"><Label>Provider</Label><Input value={newCase.insurance_provider || ''} onChange={(e) => setNewCase({ ...newCase, insurance_provider: e.target.value })} data-testid="case-insurance-provider" /></div>
                <div className="space-y-2">
                  <Label>Type of Cover</Label>
                  <Select value={newCase.insurance_cover_type || 'none'} onValueChange={(v) => setNewCase({ ...newCase, insurance_cover_type: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="case-cover-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem>{INSURANCE_COVER_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Reference Number</Label><Input value={newCase.insurance_reference || ''} onChange={(e) => setNewCase({ ...newCase, insurance_reference: e.target.value })} data-testid="case-insurance-ref" /></div>
                <div className="space-y-2"><Label>Monthly Premium (£)</Label><CurrencyInput value={newCase.monthly_premium || ''} onChange={(e) => setNewCase({ ...newCase, monthly_premium: e.target.value })} data-testid="case-monthly-premium" /></div>
                <div className="space-y-2">
                  <Label>Guaranteed or Reviewable</Label>
                  <Select value={newCase.guaranteed_or_reviewable || 'none'} onValueChange={(v) => setNewCase({ ...newCase, guaranteed_or_reviewable: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="case-guaranteed"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem><SelectItem value="guaranteed">Guaranteed</SelectItem><SelectItem value="reviewable">Reviewable</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Sum Assured (£)</Label><CurrencyInput value={newCase.sum_assured || ''} onChange={(e) => setNewCase({ ...newCase, sum_assured: e.target.value })} data-testid="case-sum-assured" /></div>
                <div className="space-y-2">
                  <Label>In Trust</Label>
                  <Select value={newCase.in_trust === true ? 'yes' : newCase.in_trust === false ? 'no' : 'none'} onValueChange={(v) => setNewCase({ ...newCase, in_trust: v === 'yes' ? true : v === 'no' ? false : null })}>
                    <SelectTrigger data-testid="case-in-trust"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Application Date</Label><Input type="date" max="9999-12-31" value={newCase.date_application_submitted || ''} onChange={(e) => setNewCase({ ...newCase, date_application_submitted: e.target.value })} /></div>
              <div className="space-y-2"><Label>Expected Completion</Label><Input type="date" max="9999-12-31" value={newCase.expected_completion_date || ''} onChange={(e) => setNewCase({ ...newCase, expected_completion_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Product Start Date</Label><Input type="date" max="9999-12-31" value={newCase.product_start_date || ''} onChange={(e) => setNewCase({ ...newCase, product_start_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Product Expiry Date</Label><Input type="date" max="9999-12-31" value={newCase.product_expiry_date || ''} onChange={(e) => setNewCase({ ...newCase, product_expiry_date: e.target.value })} /></div>
            </div>

            {/* Commission */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <h3 className="col-span-2 font-semibold text-slate-900 dark:text-slate-100 pb-1 border-b border-slate-200 dark:border-slate-700">Commission</h3>
              <div className="space-y-2"><Label>Proc Fee Total (£)</Label><CurrencyInput value={newCase.proc_fee_total || ''} onChange={(e) => setNewCase({ ...newCase, proc_fee_total: e.target.value })} data-testid="case-proc-fee" /></div>
              <div className="space-y-2"><Label>Commission Percentage (%)</Label><Input type="number" step="0.01" value={newCase.commission_percentage || ''} onChange={(e) => setNewCase({ ...newCase, commission_percentage: e.target.value })} data-testid="case-commission-pct" /></div>
              {newCase.proc_fee_total && newCase.commission_percentage && (
                <div className="col-span-2 text-sm text-green-700 font-medium">
                  Calculated Commission: {formatCurrency(Math.round((parseFloat(newCase.proc_fee_total) * parseFloat(newCase.commission_percentage) / 100) * 100) / 100)}
                </div>
              )}
              <div className="space-y-2">
                <Label>Commission Status</Label>
                <Select value={newCase.commission_status || 'pending'} onValueChange={(v) => { setNewCase({ ...newCase, commission_status: v, commission_paid_date: v !== 'paid' ? '' : newCase.commission_paid_date }); if (v === 'paid') fireConfetti(); }}>
                  <SelectTrigger data-testid="case-commission-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="clawed_back">Clawed Back</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newCase.commission_status === 'paid' ? (
                <div className="space-y-2">
                  <Label>Commission Date Paid</Label>
                  <Input type="date" max="9999-12-31" value={newCase.commission_paid_date || ''} onChange={(e) => setNewCase({ ...newCase, commission_paid_date: e.target.value })} data-testid="case-commission-paid-date" />
                </div>
              ) : <div />}

              {/* Client Fee */}
              <div className="col-span-2 border-t border-slate-200 dark:border-slate-700 pt-3 mt-1">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Client Fee</p>
              </div>
              <div className="space-y-2"><Label>Client Fee (£)</Label><CurrencyInput value={newCase.client_fee || ''} onChange={(e) => setNewCase({ ...newCase, client_fee: e.target.value })} data-testid="case-client-fee" /></div>
              <div className="space-y-2">
                <Label>Client Fee Status</Label>
                <Select value={newCase.client_fee_status || 'pending'} onValueChange={(v) => setNewCase({ ...newCase, client_fee_status: v, client_fee_paid_date: v !== 'paid' ? '' : newCase.client_fee_paid_date })}>
                  <SelectTrigger data-testid="case-client-fee-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newCase.client_fee_status === 'paid' ? (
                <div className="space-y-2">
                  <Label>Client Fee Date Paid</Label>
                  <Input type="date" max="9999-12-31" value={newCase.client_fee_paid_date || ''} onChange={(e) => setNewCase({ ...newCase, client_fee_paid_date: e.target.value })} data-testid="case-client-fee-paid-date" />
                </div>
              ) : <div />}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={newCase.status || 'new_lead'} onValueChange={(v) => setNewCase({ ...newCase, status: v })}>
                <SelectTrigger data-testid="case-status"><SelectValue /></SelectTrigger>
                <SelectContent>{CASE_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); resetNewCase(); }}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleCreateCase} disabled={!newCase.client_id || !newCase.product_type} data-testid="save-case-btn">Create Case</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cases;
